/**
 * This component is for 2 parallel buttons variant and rendered on the preview page when a funding option/transaction
 * triggers remittance transfer rule. This component displays a table of information
 * about how much the user is going to pay, how much the fees are, and how much
 * the recipient will receive,
 * it's an alternative to review-info.js with additional row which shows the seller fee with a tooltip info.
 */
import PropTypes from 'prop-types'
import React from 'react'
import classNames from 'classnames'
import { StyleSheet, css } from 'lib/paypalphrodite'
import glamorous from 'glamorous'
import content from 'pp-react-l10n'
import TooltipTarget from '../containers/vx-tooltip/target'
import { trackAction } from '../../lib/analytics/fpti'
import enRTRStrings from '../../../../config/enRTRStrings'
import { isDw2Repaint } from '../../experiments/dw2Repaint'
import {
  BodyText,
  CaptionText,
  v2black,
  v2blue400,
  v2grey600,
} from '@paypalcorp/pp-react'

let i18n = content('transfer/inc/rtr')
let i18nSelectedFundingOption = content('transfer/inc/selectedFundingOption')

const primaryText = {
  fontSize: '16px',
  color: '#2C2E2F',
  lineHeight: '24px',
}

const subText = {
  fontSize: '14px',
  color: '#687173',
  lineHeight: '20px',
  fontWeight: 'normal',
}

const styles = StyleSheet.create({
  feeInfoTooltip: {
    top: '5px',
    color: '#2c2e2f',
  },
  rtrPrimaryText: {
    ...primaryText,
  },
  rtrENSubText: {
    ...subText,
  },
})

const FeesRow = glamorous.div(
  {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: isDw2Repaint() ? 'flex-start' : 'center',
    color: isDw2Repaint() ? v2black : '#000000',
    padding: isDw2Repaint() ? '0.5rem 0' : '0 2px 12px',
  },
  ({ showENSubTextRTR }) => {
    if (showENSubTextRTR) {
      return {
        alignItems: 'flex-start',
      }
    }
  }
)

const SellerFeeContentWithTooltip = glamorous.div({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  '& button': {
    backgroundColor: 'white',
  },
  '& .vx_text-legal': {
    fontFamily: 'PayPalSansBig-Regular, "Helvetica Neue", Arial, sans-serif',
    '& a': {
      color: v2blue400,
      fontFamily: 'PayPalSansBig-Regular, "Helvetica Neue", Arial, sans-serif',
      fontWeight: 600,
    },
  },
})

const FeesBlock = glamorous.div({
  margin: 5,
})

class ReviewInfo extends React.Component {
  static propTypes = {
    isPurchase: PropTypes.bool.isRequired,
    recipient: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    whoPaysFee: PropTypes.string.isRequired,
    recipientCountry: PropTypes.string.isRequired,
    showDonateContent: PropTypes.bool,
    countryCode: PropTypes.string.isRequired,
    language: PropTypes.string,
    selectedInstallmentPlan: PropTypes.object,
    selectedFundingOption: PropTypes.shape({
      isRTR: PropTypes.bool,
      currency_exchange: PropTypes.object,
      displayAmounts: PropTypes.shape({
        paid: PropTypes.string, // the amount the sender is paying (including fees)
        received: PropTypes.string, // the amount the recipient will actually receive
        protectionFee: PropTypes.string, // Fee which buyer covers
        totalReceiverFee: PropTypes.string, // Fee which seller covers
        totalAmountReceived: PropTypes.string, // the amount seller will receive
      }).isRequired,
    }).isRequired,
  }

  handleTooltipClick() {
    trackAction('about_seller_fee')
  }

  render() {
    const {
      showDonateContent,
      isPurchase,
      selectedInstallmentPlan: { updatedTotal } = {},
      selectedFundingOption: {
        displayAmounts: { paid, protectionFee, totalAmountReceived } = {},
        isRTR,
      } = {},
      countryCode,
      language = '',
    } = this.props

    let totalAmount = updatedTotal || paid

    const feeTooltipClass = classNames(
      'ppvx_icon ppvx_icon--size_xs ppvx_icon-info',
      css(styles.feeInfoTooltip)
    )

    const showENSubTextRTR =
      isRTR === true && language !== '' && language !== 'en'

    return (
      <FeesBlock>
        {isPurchase && this.props.whoPaysFee === 'senderPaysFee' && (
          <FeesRow showENSubTextRTR={showENSubTextRTR}>
            {isDw2Repaint() ? (
              <>
                <SellerFeeContentWithTooltip data-nemo="seller-fee-content">
                  <BodyText>
                    {i18nSelectedFundingOption('copy.sellerFee')}
                  </BodyText>
                  <TooltipTarget
                    content={i18nSelectedFundingOption.html(
                      'fee.tooltip.sellerFee',
                      { countryCode }
                    )}
                    isClickable={true}
                    handleClick={this.handleTooltipClick}
                    neverShowBubble={true}
                    iconClasses={feeTooltipClass}
                    horizontalPosition="left"
                  />
                  {showENSubTextRTR && (
                    <CaptionText style={{ color: v2grey600 }}>
                      {enRTRStrings['copy.sellerFee']}
                    </CaptionText>
                  )}
                </SellerFeeContentWithTooltip>
                <BodyText data-nemo="seller-will-get">{protectionFee}</BodyText>
              </>
            ) : (
              <>
                <div className="ppvx_text--md" data-nemo="seller-fee-content">
                  <span
                    className={classNames({
                      [css(styles.rtrPrimaryText)]: showENSubTextRTR,
                    })}
                  >
                    {i18nSelectedFundingOption('copy.sellerFee')}
                  </span>
                  <TooltipTarget
                    content={i18nSelectedFundingOption.html(
                      'fee.tooltip.sellerFee',
                      { countryCode }
                    )}
                    isClickable={true}
                    handleClick={this.handleTooltipClick}
                    neverShowBubble={true}
                    iconClasses={feeTooltipClass}
                    horizontalPosition="left"
                  />
                  {showENSubTextRTR && (
                    <div className={css(styles.rtrENSubText)}>
                      {enRTRStrings['copy.sellerFee']}
                    </div>
                  )}
                </div>
                <div className="ppvx_text--md" data-nemo="seller-will-get">
                  {protectionFee}
                </div>
              </>
            )}
          </FeesRow>
        )}
        {isPurchase &&
          (isDw2Repaint() ? (
            <FeesRow showENSubTextRTR={showENSubTextRTR}>
              <div data-nemo="seller-will-get">
                <BodyText>
                  {i18nSelectedFundingOption('copy.sellerWillGet')}
                </BodyText>
                {showENSubTextRTR && (
                  <CaptionText style={{ color: v2grey600 }}>
                    {enRTRStrings['copy.sellerWillGet']}
                  </CaptionText>
                )}
              </div>
              <BodyText data-nemo="seller-will-get">
                {totalAmountReceived}
              </BodyText>
            </FeesRow>
          ) : (
            <FeesRow
              className={!isPurchase ? 'hide' : ''}
              showENSubTextRTR={showENSubTextRTR}
            >
              <div className="ppvx_text--md" data-nemo="seller-will-get">
                <span
                  className={classNames({
                    [css(styles.rtrPrimaryText)]: showENSubTextRTR,
                  })}
                >
                  {i18nSelectedFundingOption('copy.sellerWillGet')}
                </span>
                {showENSubTextRTR && (
                  <div className={css(styles.rtrENSubText)}>
                    {enRTRStrings['copy.sellerWillGet']}
                  </div>
                )}
              </div>
              <div className="ppvx_text--md" data-nemo="seller-will-get">
                {totalAmountReceived}
              </div>
            </FeesRow>
          ))}
        {isDw2Repaint() ? (
          <FeesRow showENSubTextRTR={showENSubTextRTR}>
            <div data-nemo="seller-fee-content">
              <BodyText>
                {showDonateContent
                  ? i18n('rtr.youllDonate')
                  : i18nSelectedFundingOption('sendDetail.yourTotal')}
              </BodyText>
              {showENSubTextRTR && (
                <CaptionText style={{ color: v2grey600 }}>
                  {showDonateContent
                    ? enRTRStrings['rtr.youllDonate']
                    : enRTRStrings['sendDetail.yourTotal']}
                </CaptionText>
              )}
            </div>
            <BodyText data-nemo="total-sender-pay">{totalAmount}</BodyText>
          </FeesRow>
        ) : (
          <FeesRow showENSubTextRTR={showENSubTextRTR}>
            <div
              className="ppvx_text--md ppvx_text--medium"
              data-nemo="seller-fee-content"
            >
              <span
                className={classNames({
                  [css(styles.rtrPrimaryText)]: showENSubTextRTR,
                })}
              >
                {showDonateContent
                  ? i18n('rtr.youllDonate')
                  : i18nSelectedFundingOption('sendDetail.yourTotal')}
              </span>
              {showENSubTextRTR && (
                <div
                  className={classNames(
                    'ppvx_text--md',
                    css(styles.rtrENSubText)
                  )}
                >
                  {showDonateContent
                    ? enRTRStrings['rtr.youllDonate']
                    : enRTRStrings['sendDetail.yourTotal']}
                </div>
              )}
            </div>
            <div
              className="ppvx_text--md ppvx_text--medium"
              data-nemo="total-sender-pay"
            >
              {totalAmount}
            </div>
          </FeesRow>
        )}
      </FeesBlock>
    )
  }
}

export default ReviewInfo
